<template>
  <div class="user-services-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <div>
        <h2>服务进行中</h2>
        <p>跟踪用户当前服务状态、题目生成进度和超时风险</p>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-icon class="is-loading">
        <Loading />
      </el-icon>
      <span>加载中...</span>
    </div>

    <!-- 数据展示 -->
    <div v-else-if="userOngoingServices">
      <!-- 统计卡片 -->
      <div class="statistics-cards">
        <el-card class="stat-card total">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><DocumentCopy /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">服务总数</div>
              <div class="stat-value">{{ userOngoingServices.total }}</div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card special">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><ChatDotRound /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">专项面试</div>
              <div class="stat-value">
                {{ userOngoingServices.specialInterviewCount }}
              </div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card behavior">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><UserFilled /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">行测+HR面试</div>
              <div class="stat-value">
                {{ userOngoingServices.behaviorInterviewCount }}
              </div>
            </div>
          </div>
        </el-card>

        <el-card class="stat-card resume">
          <div class="stat-content">
            <div class="stat-icon">
              <el-icon><Document /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-label">简历押题</div>
              <div class="stat-value">
                {{ userOngoingServices.resumeQuizCount }}
              </div>
            </div>
          </div>
        </el-card>
      </div>

      <!-- 筛选器 -->
      <div class="filter-section">
        <el-select
          v-model="filterServiceType"
          placeholder="筛选服务类型"
          clearable
          style="width: 200px"
        >
          <el-option label="全部" value="" />
          <el-option label="专项面试" value="special_interview" />
          <el-option label="行测+HR面试" value="behavior_interview" />
          <el-option label="简历押题" value="resume_quiz" />
        </el-select>

        <el-select
          v-model="filterStatus"
          placeholder="筛选状态"
          clearable
          style="width: 200px; margin-left: 10px"
        >
          <el-option label="全部" value="" />
          <el-option label="进行中" value="in_progress" />
          <el-option label="待开始" value="pending" />
        </el-select>

        <el-select
          v-model="filterDuration"
          placeholder="筛选持续时间"
          clearable
          style="width: 200px; margin-left: 10px"
        >
          <el-option label="全部" value="" />
          <el-option label="不足2小时" value="under_2h" />
          <el-option label="超过2小时" value="over_2h" />
        </el-select>
      </div>

      <!-- 服务列表表格 -->
      <el-card class="table-card">
        <el-table :data="filteredData" stripe style="width: 100%">
          <!-- 用户信息 -->
          <el-table-column label="用户" width="200">
            <template #default="{ row }">
              <div class="user-info">
                <el-avatar :src="row.user.avatar" :size="40" />
                <div class="user-details">
                  <div class="username">{{ row.user.username }}</div>
                  <div class="user-email">{{ row.user.email }}</div>
                </div>
              </div>
            </template>
          </el-table-column>

          <!-- 服务类型 -->
          <el-table-column label="服务类型" width="150">
            <template #default="{ row }">
              <el-tag
                :type="getServiceTypeColor(row.serviceTypeCode)"
                effect="dark"
              >
                {{ row.serviceType }}
              </el-tag>
            </template>
          </el-table-column>

          <!-- 职位信息 -->
          <el-table-column label="职位信息" width="200">
            <template #default="{ row }">
              <div class="job-info">
                <div v-if="row.company" class="company">
                  <el-icon><OfficeBuilding /></el-icon>
                  {{ row.company }}
                </div>
                <div v-if="row.position" class="position">
                  <el-icon><Briefcase /></el-icon>
                  {{ row.position }}
                </div>
                <div v-if="!row.company && !row.position" class="empty">-</div>
              </div>
            </template>
          </el-table-column>

          <!-- 问题进度 -->
          <el-table-column label="问题进度" width="180">
            <template #default="{ row }">
              <div class="question-progress">
                <div class="progress-text">
                  已生成: {{ row.generatedQuestions }} /
                  {{ row.totalQuestions }}
                </div>
                <el-progress
                  :percentage="
                    getProgressPercentage(
                      row.generatedQuestions,
                      row.totalQuestions
                    )
                  "
                  :color="
                    getProgressColor(row.generatedQuestions, row.totalQuestions)
                  "
                  :stroke-width="6"
                />
                <div class="remaining-text">
                  剩余: {{ row.remainingQuestions }} 题
                </div>
              </div>
            </template>
          </el-table-column>

          <!-- 状态 -->
          <el-table-column label="状态" width="100">
            <template #default="{ row }">
              <el-tag :type="getStatusColor(row.status)">
                {{ getStatusText(row.status) }}
              </el-tag>
            </template>
          </el-table-column>

          <!-- 持续时间 -->
          <el-table-column label="持续时间" width="150">
            <template #default="{ row }">
              <div
                class="duration"
                :class="{ 'duration-warning': row.durationMinutes > 120 }"
              >
                <el-icon><Timer /></el-icon>
                {{ formatDuration(row.durationMinutes) }}
                <el-tooltip
                  v-if="row.durationMinutes > 120"
                  content="持续时间超过2小时"
                  placement="top"
                >
                  <el-icon class="warning-icon"><WarningFilled /></el-icon>
                </el-tooltip>
              </div>
            </template>
          </el-table-column>

          <!-- 创建时间 -->
          <el-table-column label="创建时间" width="180">
            <template #default="{ row }">
              {{ formatDate(row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column
            fixed="right"
            label="服务 ID"
            width="180"
            prop="serviceId"
          ></el-table-column>

          <!-- 操作 -->
          <!-- <el-table-column label="操作" width="120" fixed="right">
            <template #default="{ row }">
              <el-button
                type="primary"
                size="small"
                link
                @click="viewDetail(row)"
              >
                查看服务 ID
              </el-button>
            </template>
          </el-table-column> -->
        </el-table>
      </el-card>
    </div>

    <!-- 无数据 -->
    <div v-else class="no-data">
      <el-empty description="暂无数据" />
    </div>
  </div>
</template>

<script setup>
import { getUserOngoingServices } from '@/api/user'
import { ref, computed } from 'vue'
import {
  DocumentCopy,
  ChatDotRound,
  UserFilled,
  Document,
  OfficeBuilding,
  Briefcase,
  Timer,
  Loading,
  WarningFilled
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

const userOngoingServices = ref(null)
const loading = ref(true)
const filterServiceType = ref('')
const filterStatus = ref('')
const filterDuration = ref('')

// 获取数据
const getUserOngoingServicesData = async () => {
  try {
    loading.value = true
    userOngoingServices.value = await getUserOngoingServices()
  } catch (error) {
    console.error('获取数据失败:', error)
    ElMessage.error('获取数据失败')
  } finally {
    loading.value = false
  }
}

// 过滤后的数据
const filteredData = computed(() => {
  if (!userOngoingServices.value?.data) return []

  let data = userOngoingServices.value.data

  if (filterServiceType.value) {
    data = data.filter(
      (item) => item.serviceTypeCode === filterServiceType.value
    )
  }

  if (filterStatus.value) {
    data = data.filter((item) => item.status === filterStatus.value)
  }

  if (filterDuration.value) {
    if (filterDuration.value === 'under_2h') {
      data = data.filter((item) => item.durationMinutes <= 120)
    } else if (filterDuration.value === 'over_2h') {
      data = data.filter((item) => item.durationMinutes > 120)
    }
  }

  return data
})

// 获取服务类型颜色
const getServiceTypeColor = (typeCode) => {
  const colorMap = {
    special_interview: 'primary',
    behavior_interview: 'success',
    resume_quiz: 'warning'
  }
  return colorMap[typeCode] || 'info'
}

// 获取状态颜色
const getStatusColor = (status) => {
  const colorMap = {
    in_progress: 'success',
    pending: 'warning',
    completed: 'info',
    cancelled: 'danger'
  }
  return colorMap[status] || 'info'
}

// 获取状态文本
const getStatusText = (status) => {
  const textMap = {
    in_progress: '进行中',
    pending: '待开始',
    completed: '已完成',
    cancelled: '已取消'
  }
  return textMap[status] || status
}

// 计算进度百分比
const getProgressPercentage = (generated, total) => {
  if (total === 0) return 0
  return Math.round((generated / total) * 100)
}

// 获取进度条颜色
const getProgressColor = (generated, total) => {
  const percentage = getProgressPercentage(generated, total)
  if (percentage === 100) return '#67c23a'
  if (percentage >= 50) return '#409eff'
  return '#e6a23c'
}

// 格式化持续时间
const formatDuration = (minutes) => {
  if (!minutes) return '-'

  const days = Math.floor(minutes / (60 * 24))
  const hours = Math.floor((minutes % (60 * 24)) / 60)
  const mins = minutes % 60

  const parts = []
  if (days > 0) parts.push(`${days}天`)
  if (hours > 0) parts.push(`${hours}时`)
  if (mins > 0) parts.push(`${mins}分`)

  return parts.join('') || '0分'
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 初始化加载数据
getUserOngoingServicesData()
</script>

<style scoped lang="scss">
.user-services-container {
  padding: 0;
  background-color: transparent;
  min-height: 100%;

  .page-header {
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #303133;
    }
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    color: #909399;

    .el-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    span {
      font-size: 16px;
    }
  }

  .statistics-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;
    margin-bottom: 20px;

    .stat-card {
      cursor: pointer;
      transition: transform 0.3s, box-shadow 0.3s;

      .stat-content {
        display: flex;
        align-items: center;
        gap: 16px;

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
        }

        .stat-info {
          flex: 1;

          .stat-label {
            font-size: 14px;
            color: #909399;
            margin-bottom: 8px;
          }

          .stat-value {
            font-size: 28px;
            font-weight: 600;
            color: #303133;
          }
        }
      }

      &.total .stat-icon {
        color: var(--admin-color-primary);
      }

      &.special .stat-icon {
        color: var(--admin-color-primary);
      }

      &.behavior .stat-icon {
        color: var(--admin-color-primary);
      }

      &.resume .stat-icon {
        color: var(--admin-color-primary);
      }
    }
  }

  .filter-section {
    margin-bottom: 20px;
    display: flex;
    gap: 10px;
  }

  .table-card {
    .user-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .user-details {
        flex: 1;

        .username {
          font-weight: 500;
          color: #303133;
          margin-bottom: 4px;
        }

        .user-email {
          font-size: 12px;
          color: #909399;
        }
      }
    }

    .job-info {
      .company,
      .position {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 13px;
        color: #606266;
        margin-bottom: 4px;

        .el-icon {
          font-size: 14px;
          color: #909399;
        }
      }

      .empty {
        color: #c0c4cc;
      }
    }

    .question-progress {
      .progress-text {
        font-size: 13px;
        color: #606266;
        margin-bottom: 4px;
      }

      .remaining-text {
        font-size: 12px;
        color: #909399;
        margin-top: 4px;
      }
    }

    .duration {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 13px;
      color: #606266;

      .el-icon {
        font-size: 14px;
        color: #909399;
      }

      &.duration-warning {
        color: #e6a23c;
        font-weight: 500;

        .el-icon {
          color: #e6a23c;
        }

        .warning-icon {
          color: #f56c6c;
          animation: pulse 2s infinite;
        }
      }
    }

    @keyframes pulse {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  }

  .no-data {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background: white;
    border-radius: 8px;
  }
}
</style>
